// The one place that knows the session cookies' names and where they live.
//
// Every session cookie is written on the parent domain (REACT_APP_DOMAIN_NAME,
// e.g. l5flt.com) so all the apps share it. Removal clears both the
// parent-domain cookie and a host-only one of the same name: a cookie can
// only be removed with the domain it was set with, and older code set some of
// them host-only (removing with the app's URL as the domain removed nothing).

import Cookies from "js-cookie";
import { cookiePrefixFor, effectiveParentDomain, getSessionConfig, ResolvedSessionConfig } from "./config";

export type SessionCookieNames = {
	/** the Cognito ID token (named accessToken for historical reasons) */
	idToken: string;
	/** legacy JS-readable refresh token: no longer written, only removed */
	legacyRefreshToken: string;
	/** the HttpOnly refresh cookie set by POST /auth/session (not readable here) */
	httpOnlyRefresh: string;
	companyId: string;
	companyName: string;
	userId: string;
};

export const cookieNamesFor = (prefix: string): SessionCookieNames => ({
	idToken: `${prefix}l5_accessToken`,
	legacyRefreshToken: `${prefix}l5_refreshToken`,
	httpOnlyRefresh: `${prefix}l5_refresh`,
	companyId: "l5_companyId",
	companyName: "l5_companyName",
	userId: "l5_userId",
});

/**
 * Cookie names for a stage, by its cookie parent domain (no configuration
 * needed). Like the session config, the page's host wins when it is under a
 * known parent domain (see effectiveParentDomain).
 */
export const cookieNamesForDomain = (domainName: string | undefined): SessionCookieNames =>
	cookieNamesFor(cookiePrefixFor(effectiveParentDomain(domainName)));

// Before the shared layer most apps used preprod_ on integ (l5flt.com) too.
// Those names are only removed (sign-in/sign-out), never read.
const legacyNames = (config: ResolvedSessionConfig): string[] => {
	if (config.cookiePrefix === "preprod_" || config.cookiePrefix === "prod_") return [];
	const old = cookieNamesFor("preprod_");
	return [old.idToken, old.legacyRefreshToken];
};

const names = (config?: ResolvedSessionConfig) => cookieNamesFor((config ?? getSessionConfig()).cookiePrefix);

const domainOf = (config?: ResolvedSessionConfig) => (config ?? getSessionConfig()).domainName || undefined;

const get = (name: string): string | undefined => Cookies.get(name) || undefined;

const set = (name: string, value: string, config?: ResolvedSessionConfig): void => {
	Cookies.set(name, value, { domain: domainOf(config) });
};

const remove = (name: string, config?: ResolvedSessionConfig): void => {
	const domain = domainOf(config);
	if (domain) Cookies.remove(name, { domain });
	Cookies.remove(name);
};

export const cookies = {
	names: (config?: ResolvedSessionConfig): SessionCookieNames => names(config),
	get,
	set,
	remove,
	getIdToken: (config?: ResolvedSessionConfig) => get(names(config).idToken),
	setIdToken: (token: string, config?: ResolvedSessionConfig) => set(names(config).idToken, token, config),
	removeIdToken: (config?: ResolvedSessionConfig) => remove(names(config).idToken, config),
	getCompanyId: () => get(names().companyId),
	getCompanyName: () => get(names().companyName),
	getUserId: () => get(names().userId),
	/**
	 * Removes the session's cookies: ID token, the legacy refresh-token
	 * cookie (+ localStorage.refreshToken), company id/name and user id.
	 */
	clearSession: (config?: ResolvedSessionConfig): void => {
		const resolved = config ?? getSessionConfig();
		const n = names(resolved);
		[n.idToken, n.legacyRefreshToken, n.companyId, n.companyName, n.userId, ...legacyNames(resolved)].forEach(
			(name) => remove(name, resolved)
		);
		removeLegacyRefreshStorage();
	},
	/** removes only the legacy JS-readable refresh token (cookie + localStorage) */
	clearLegacyRefreshToken: (config?: ResolvedSessionConfig): void => {
		const resolved = config ?? getSessionConfig();
		[names(resolved).legacyRefreshToken, ...legacyNames(resolved)].forEach((name) => remove(name, resolved));
		removeLegacyRefreshStorage();
	},
};

function removeLegacyRefreshStorage() {
	try {
		window.localStorage.removeItem("refreshToken");
	} catch {
		// storage unavailable: nothing stored
	}
}
